import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save } from "lucide-react";

interface KBEntry {
  id: string;
  title: string;
  content: string;
  scope: "global" | "sport_specific";
  sport_id: string | null;
  tags: string[] | null;
  is_active: boolean;
}

interface Sport {
  id: string;
  name: string;
}

const AdminKnowledge = () => {
  const [entries, setEntries] = useState<KBEntry[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<KBEntry | null>(null);
  const [editEntry, setEditEntry] = useState<Partial<KBEntry>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [{ data: kb }, { data: sp }] = await Promise.all([
      supabase.from("knowledge_base_entries").select("*").order("created_at", { ascending: false }),
      supabase.from("sports").select("id, name").eq("is_active", true).order("name"),
    ]);
    if (kb) setEntries(kb as KBEntry[]);
    if (sp) setSports(sp as Sport[]);
    setLoading(false);
  };

  const selectEntry = (e: KBEntry) => {
    setSelected(e);
    setEditEntry({ ...e });
  };

  const handleNew = () => {
    const newEntry: Partial<KBEntry> = {
      title: "",
      content: "",
      scope: "global",
      sport_id: null,
      tags: [],
      is_active: true,
    };
    setSelected(null);
    setEditEntry(newEntry);
  };

  const handleSave = async () => {
    if (!editEntry.title || !editEntry.content) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);

    if (selected) {
      // Update
      const { error } = await supabase
        .from("knowledge_base_entries")
        .update({
          title: editEntry.title,
          content: editEntry.content,
          scope: editEntry.scope as any,
          sport_id: editEntry.scope === "sport_specific" ? editEntry.sport_id : null,
          tags: editEntry.tags,
          is_active: editEntry.is_active,
        })
        .eq("id", selected.id);
      if (error) toast.error("Failed to save");
      else toast.success("Entry updated");
    } else {
      // Insert
      const { error } = await supabase.from("knowledge_base_entries").insert({
        title: editEntry.title!,
        content: editEntry.content!,
        scope: (editEntry.scope || "global") as any,
        sport_id: editEntry.scope === "sport_specific" ? editEntry.sport_id : null,
        tags: editEntry.tags,
        is_active: editEntry.is_active ?? true,
      });
      if (error) toast.error("Failed to create");
      else toast.success("Entry created");
    }

    setSaving(false);
    loadData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this entry?")) return;
    await supabase.from("knowledge_base_entries").delete().eq("id", id);
    toast.success("Entry deleted");
    if (selected?.id === id) {
      setSelected(null);
      setEditEntry({});
    }
    loadData();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Knowledge Base</h1>
            <p className="text-muted-foreground mt-1">
              Global and sport-specific knowledge used to enrich AI-generated scripts.
            </p>
          </div>
          <Button onClick={handleNew}>
            <Plus className="w-4 h-4 mr-2" /> New Entry
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {entries.length === 0 && (
              <p className="text-muted-foreground text-sm py-8 text-center">
                No entries yet. Click "New Entry" to add one.
              </p>
            )}
            {entries.map((e) => (
              <button
                key={e.id}
                onClick={() => selectEntry(e)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selected?.id === e.id
                    ? "border-primary bg-primary/5"
                    : "border-border/50 bg-secondary/30 hover:border-primary/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm truncate">{e.title}</span>
                  <button
                    onClick={(ev) => {
                      ev.stopPropagation();
                      handleDelete(e.id);
                    }}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    e.scope === "global" ? "bg-blue-500/10 text-blue-500" : "bg-orange-500/10 text-orange-500"
                  }`}>
                    {e.scope === "global" ? "Global" : "Sport-specific"}
                  </span>
                  {!e.is_active && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      Inactive
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Editor */}
          <div className="lg:col-span-2 space-y-4">
            {(selected || editEntry.title !== undefined) ? (
              <>
                <input
                  value={editEntry.title || ""}
                  onChange={(e) => setEditEntry({ ...editEntry, title: e.target.value })}
                  placeholder="Entry title..."
                  className="w-full h-12 rounded-xl border border-border/50 bg-secondary/30 px-4 text-foreground font-medium focus:outline-none focus:ring-2 focus:ring-ring"
                />

                <div className="flex gap-4">
                  <select
                    value={editEntry.scope || "global"}
                    onChange={(e) => setEditEntry({ ...editEntry, scope: e.target.value as any })}
                    className="rounded-lg border border-border/50 bg-secondary/30 px-3 py-2 text-sm text-foreground"
                  >
                    <option value="global">Global</option>
                    <option value="sport_specific">Sport-specific</option>
                  </select>

                  {editEntry.scope === "sport_specific" && (
                    <select
                      value={editEntry.sport_id || ""}
                      onChange={(e) => setEditEntry({ ...editEntry, sport_id: e.target.value || null })}
                      className="rounded-lg border border-border/50 bg-secondary/30 px-3 py-2 text-sm text-foreground"
                    >
                      <option value="">Select sport...</option>
                      {sports.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  )}

                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editEntry.is_active ?? true}
                      onChange={(e) => setEditEntry({ ...editEntry, is_active: e.target.checked })}
                      className="rounded"
                    />
                    Active
                  </label>
                </div>

                <textarea
                  value={editEntry.content || ""}
                  onChange={(e) => setEditEntry({ ...editEntry, content: e.target.value })}
                  placeholder="Knowledge content..."
                  className="w-full h-[400px] rounded-xl border border-border/50 bg-secondary/30 p-4 text-sm text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />

                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </>
            ) : (
              <div className="flex items-center justify-center py-20 text-muted-foreground">
                Select an entry or create a new one.
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminKnowledge;
